import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";

import { envConfig } from "./config";
import { getAuth, removeAuth, PERMISSIONS_STALE_EVENT } from "../app/modules/auth";
import { ApiResponse } from "../app/modules/core/_models";
import { generateHmacSHA256 } from "./hmac";
import { getDefaultMessageForStatus } from "./utils";
import { SnackbarService } from "../app/services/SnackbarService";

const UNAUTHORIZED_STATUS = 401;
const LOGIN_ENDPOINT = "/login";

/* =========================
   DEFAULT CONFIG
========================= */

const DEFAULT_CONFIG: AxiosRequestConfig = {
  headers: {
    "Content-Type": "multipart/form-data",
  },
};

/* =========================
   AXIOS FACTORY
========================= */

const createApiClient = (baseURL: string): AxiosInstance =>
  axios.create({
    baseURL,
    ...DEFAULT_CONFIG,
  });

/* =========================
   API CLIENTS
========================= */
const eveApiClient = createApiClient(
  envConfig.APP_EVE_API_URL
);


// API Laravel instance
const apiLaravel = axios.create({
  baseURL: envConfig.APP_PLATFORM_API_URL + "/api",
  headers: { "Content-Type": "application/json" },
  timeout: 30000, // 30 second timeout
});


/* =========================
   CONFIG MERGER
========================= */

const mergeConfig = (
  config?: AxiosRequestConfig
): AxiosRequestConfig => ({
  ...DEFAULT_CONFIG,
  ...config,
  headers: {
    ...(DEFAULT_CONFIG.headers || {}),
    ...(config?.headers || {}),
  },
});


/* =========================
   COMMON POST HELPER
========================= */

async function postRequest<T>(
  client: AxiosInstance,
  url: string,
  payload: any,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await client.post<T>(
    url,
    payload,
    mergeConfig(config)
  );

  return response.data;
}

/* =========================
   EVE REQUEST
========================= */

export async function eveRequest<
  TResponse,
  TPayload extends object = Record<string, unknown>
>(
  path: string,
  payload: TPayload,
  config?: AxiosRequestConfig
): Promise<TResponse> {
  const user = getAuth();

  const finalPayload = {
    ...payload,
    userid: user?.user_id,
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  return postRequest<TResponse>(
    eveApiClient,
    `/api${path}`,
    finalPayload,
    config
  );
}

/**
 * Build base payload with common fields
 */
const buildBasePayloadWithEncryption = async (data: Record<string, any>): Promise<Record<string, any>> => {
  const [hmac] = await Promise.all([
    generateHmacSHA256(envConfig.APP_API_CLIENT_DOMAIN, envConfig.APP_API_CLIENT_KEY),
  ]);
  let payload: Record<string, any> = {
    ...data,
    clid: hmac,
    serverName: envConfig.APP_API_CLIENT_DOMAIN,
  };
  if (window.CookieUtils.getMode() === "secure") {
    payload = {
      param: window.CookieUtils.encode(JSON.stringify(payload), envConfig.APP_API_SECRET),
    };
  }

  return payload;
};


const isRequestAborted = (error: AxiosError<ApiResponse>): boolean => {
  return (
    error.code === "ERR_CANCELED" ||
    error.name === "CanceledError" ||
    error.name === "AbortError" ||
    axios.isCancel(error)
  );
};

/**
 * Handle API errors
 */
const handleApiErrorLaravel = (error: AxiosError<ApiResponse>, endpoint: string): never => {
  const { response, message } = error;

  // Aborted requests should be silent — callers decide whether to ignore
  if (isRequestAborted(error)) {
    throw error;
  }

  // Network error or no response
  if (!response) {
    SnackbarService.show(message || "Unexpected error occurred", "danger");
    throw error;
  }

  const { status, data } = response;
  const errorMessage = data?.message || getDefaultMessageForStatus(status);

  // Handle unauthorized errors (non-login only)
  if (status === UNAUTHORIZED_STATUS && endpoint !== LOGIN_ENDPOINT) {
    SnackbarService.show(errorMessage, "danger");
      removeAuth();
      window.location.href = "/auth/login";
    throw error;
  }

  // Our cached rights disagree with the server: let AuthProvider refresh them.
  if (data?.errorCode === "PERMISSION_DENIED") {
    window.dispatchEvent(new Event(PERMISSIONS_STALE_EVENT));
  }

  // Throw API response body so callers can use error.message in catch
  throw data;
};



// Request interceptor
apiLaravel.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const userData = await getAuth();

    if (userData?.api_token) {
      config.headers.Authorization = `Bearer ${userData.api_token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Main request function with optimizations
 */
export const requestLaravel = async <T>(
  endpoint: string,
  data: Record<string, any> = {},
  config: AxiosRequestConfig = {}
): Promise<T> => {
  try {
    const payloadPromise = buildBasePayloadWithEncryption(data);
    const [basePayload] = await Promise.all([payloadPromise]);
    const response = await apiLaravel.post<ApiResponse<T>>(endpoint, basePayload, {
      headers: { "Content-Type": "multipart/form-data" },
      ...config,
    });

    if (window.CookieUtils.getMode() === "secure") {
      // @ts-ignore
      return JSON.parse(window.CookieUtils.decodefn(response.data.param, envConfig.APP_API_SECRET)) as T;
    }
    return response.data as T;
  } catch (error: any) {
    return handleApiErrorLaravel(error, endpoint);
  }
};
